import { TOKEN } from '@/constants';
import {
  fetchReportedDistributorRecord,
  fetchUserClickBuriedPoint,
  fetchAppConfigInit,
  fetchUserDataInit,
  fetchAddCustomerSourceMiniProgram,
} from '@/services';
import wechatH5, { initH5WechatSDKConfig } from '@/services/login/wechatH5';
import wechatMp from '@/services/login/wechatMp';
import { useAppStore, useUserStore, useMembershipLevelStore, useCommonStore } from '@/store';
import { nextTick } from 'vue';
import { businessUtils } from '.';
import { commonUtils } from '../common';
import { storage } from '../storage';
import { wechatMpSceneConfig } from '@/config';

const uniLogin = async () => {
  // #ifdef H5
  if (commonUtils.mp.isH5()) {
    await wechatH5();
  }
  // #endif
  // #ifdef MP-WEIXIN
  await wechatMp();
  // #endif
};

/**
 * 登录前处理
 * 优化后：使用合并查询 AppConfigInit 替代 3 
 * - settings (精选商品配置)
 * - customActiveChannel (店铺配置)
 * - getWeappDynamicRouteConfig (tabbar和功能页面配置)
 * - maxDiscountMembershipPlan (最大优惠会员卡)
 * - getLevelConfig (会员等级配置)
 */
const beforeHandler = async () => {
  await nextTick();
  const userStore = useUserStore();
  const membershipLevelStore = useMembershipLevelStore();
  const appStore = useAppStore();

  try {
    // 使用合并查询获取所有配置（带 5 分钟 TTL 缓存）
    const appConfigData = await fetchAppConfigInit();

    if (appConfigData) {
      // 批量设置 app store 数据
      appStore.setFromAppConfigInit({
        settings: appConfigData.settings,
        activeChannel: appConfigData.activeChannel,
        getWeappDynamicRouteConfig: appConfigData.getWeappDynamicRouteConfig,
      });

      // 设置最大优惠会员卡
      userStore.setMaxDiscountMembershipPlanFromInit(appConfigData.maxDiscountMembershipPlan);

      // 设置会员等级配置
      membershipLevelStore.setFromInit({
        getLevelConfig: appConfigData.getLevelConfig,
      });
    }
  } catch (error) {
    console.error('[beforeHandler] AppConfigInit 请求失败，降级为单独请求:', error);
    // 降级处理：使用原有的单独请求
    await Promise.all([
      appStore.getSettings(true),
      appStore.getShopConfigs(true),
      appStore.getWeappDynamicRouteConfig(),
      userStore.getMaxDiscountMembershipPlan(true),
      membershipLevelStore.getLevelConfigList(),
    ]);
  }
};

/**
 * 登录后处理
 * 优化后：
 * - 第一步：使用合并查询 UserDataInit 替代 5 个串行请求
 *   - activeCustomer (用户信息)
 *   - getUserMember (会员信息)
 *   - getVirtualCurrencyBalance (虚拟币余额)
 *   - getOwnCustomlevel (会员等级)
 *   - getDistributor (分销员数据)
 * - 第二步：剩余请求并行执行
 */
const afterHandler = async () => {
  await nextTick();
  console.log('afterHandler');

  const appStore = useAppStore();
  const userStore = useUserStore();
  const membershipLevelStore = useMembershipLevelStore();
  const commonStore = useCommonStore();

  // ===== 第一步：合并查询用户数据 =====
  try {
    const userDataInit = await fetchUserDataInit();

    if (userDataInit) {
      // 批量设置用户 store 数据
      userStore.setFromUserDataInit({
        activeCustomer: userDataInit.activeCustomer,
        getUserMember: userDataInit.getUserMember,
        getVirtualCurrencyBalance: userDataInit.getVirtualCurrencyBalance,
      });

      // 设置会员等级数据
      membershipLevelStore.setFromInit({
        getOwnCustomlevel: userDataInit.getOwnCustomlevel,
      });

      // 设置分销员数据
      appStore.setDistributorFromInit(userDataInit.getDistributor);
    }
  } catch (error) {
    console.error('[afterHandler] UserDataInit 请求失败，降级为单独请求:', error);
    // 降级处理：使用原有的单独请求（并行执行）
    await Promise.all([
      userStore.getUserInfo(true),
      userStore.getUserMemberInfo(true),
      userStore.getVirtualCurrencyBalance(true),
      membershipLevelStore.getOwnCustomLevel(true),
      appStore.getGetDistributor(true),
    ]);
  }

  // 分销上报
  businessUtils.outsideComein.distribution();

  // 补报缓存的场景值（未登录时存储，登录后消费）
  businessUtils.mp.scene();

  // ===== 第二步：剩余请求并行执行 =====
  // 注意：settings 和 shopConfigs 已在 beforeHandler 的 AppConfigInit 中获取
  await Promise.all([
    appStore.getShoppingTrolleyCount(),
    appStore.getSubscribeMessageTemplateId(),
    appStore.getCustomNoticeSubscribeScenes(),
    fetchUserClickBuriedPoint(),
    appStore.getShareSettingPageKey(),
    commonStore.getUnusedCouponList(true),
  ]);
};

export default {
  watch: async () => {
    let hasExecutedAfterHandler = false;

    useUserStore().$subscribe(async (mutation, state) => {
      if (state.isLogin && !hasExecutedAfterHandler) {
        hasExecutedAfterHandler = true;
        await afterHandler();
      }
    });
    // 执行登录之前的请求、操作
    await beforeHandler();
  },
  before: beforeHandler,
  after: afterHandler,
  check: async () => {
    try {
      const token = storage.get(TOKEN);
      console.log('[token]', token);

      // 判断是否存在或过期
      if (!token) {
        await uniLogin();
      } else {
        // 设置登录标识
        useUserStore().isLogin = true;

        // 上报enterOptions
        const enterOptions = uni.getEnterOptionsSync();
        fetchReportedDistributorRecord(
          enterOptions.query?.scene ?? '',
          enterOptions.scene + '',
          enterOptions.query?.distributorId ?? ''
        );
      }

      // #ifdef H5
      initH5WechatSDKConfig();
      // #endif
    } catch (error) {
      /** */
    }
  },
  exection: uniLogin,
};
